import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
    ViewEncapsulation,
    input,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import {
    SocChatExternalTypeValue,
    SocChannelType,
    SocJwtResponse,
    StandoutClassroomService,
} from "../services/standout.classroom.service";
import { buildSocChatEmbedPath } from "../services/soc.chat.channel.util";
import { S25Util } from "../util/s25-util";
import { TypeManagerDecorator } from "../main/type.map.service";
import { EngagementService } from "../modules/student-engagement/engagement-metrics/engagement.service";
import { SeSecurityStore } from "../modules/student-engagement/se.security.store";

@TypeManagerDecorator("s25-chat")
@Component({
    selector: "s25-chat",
    template: `
        @if (iframeUrl) {
            <div data-testid="seChatComponent" class="chat-wrapper">
                <iframe
                    [src]="iframeUrl"
                    frameborder="0"
                    allow="microphone; camera; clipboard-read; clipboard-write"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    aria-label="Chat"
                    (error)="onIframeError()"
                ></iframe>
                @if (showFallback) {
                    <div class="chat-fallback">
                        <div class="fallback-content">
                            <p>Chat cannot be embedded due to security restrictions.</p>
                            <a [href]="getChatLink()" target="_blank" class="btn btn-primary"> Open Chat </a>
                        </div>
                    </div>
                }
            </div>
        } @else if (loadError) {
            <div class="chat-loading">
                <span
                    >Chat is unavailable. Your account may be missing required information. Please contact your
                    administrator.</span
                >
            </div>
        } @else {
            <div class="chat-loading">
                <span>Loading chat...</span>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: block;
                min-height: 600px;
            }

            :host-context(.chat-content) {
                min-height: unset;
                height: 100%;
            }

            .chat-wrapper {
                height: 100%;
                border: 1px solid #ddd;
                border-radius: 4px;
                overflow: hidden;
            }

            .chat-loading {
                display: flex;
                align-items: center;
                justify-content: center;
                color: #666;
                font-style: italic;
            }

            .chat-fallback {
                position: absolute;
                top: 0;
                left: 0;
                background: rgba(255, 255, 255, 0.95);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10;
            }

            .fallback-content {
                text-align: center;
                padding: 20px;
            }

            .fallback-content p {
                margin-bottom: 15px;
                color: #666;
            }

            .btn {
                padding: 8px 16px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
            }

            .btn-primary {
                background-color: #007bff;
                color: white;
            }

            .btn-primary:hover {
                background-color: #0056b3;
            }

            iframe {
                border: none;
                width: 100%;
                height: 600px;
                display: block;
            }

            :host-context(.chat-content) iframe {
                height: 100%;
            }

            ::ng-deep .nm-party--on .chat-wrapper {
                border-color: rgba(255, 255, 255, 0.24);
            }

            ::ng-deep .nm-party--on .chat-loading {
                color: #ccc;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
})
export class S25ChatComponent implements OnInit {
    channelId = input<string>();
    channelTitle = input<string>();
    channelType = input<SocChannelType>("private");
    socChannelId = input<number | undefined>(undefined);
    externalType = input<SocChatExternalTypeValue | undefined>(undefined);

    // iframe URL - updated when token refreshes
    iframeUrl: SafeResourceUrl | null = null;

    // Flag to show fallback UI when iframe fails
    showFallback: boolean = false;
    loadError: boolean = false;

    private readonly securityStore = inject(SeSecurityStore);

    constructor(
        private cd: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
    ) {}

    async ngOnInit() {
        if (!this.securityStore.canAccessChat()) {
            this.loadError = true;
            this.cd.detectChanges();
            return;
        }
        EngagementService.recordActivity("CHAT_OPEN").catch(() => {});
        await this.initializeChat();
    }

    private async initializeChat() {
        if (!this.securityStore.canAccessChat()) {
            this.loadError = true;
            return;
        }
        try {
            // Get JWT token from StandoutClassroomService
            const tokenHolder = await StandoutClassroomService.getAuthToken();

            if (!tokenHolder) {
                console.error("Chat: failed to obtain auth token");
                this.loadError = true;
                S25Util.showError(
                    "Chat is unavailable. Your account may be missing required information. Please contact your administrator.",
                );
                return;
            }

            // Build iframe URL with token
            this.buildIframeUrl(tokenHolder);
        } catch (error) {
            console.error("Error initializing chat component:", error);
            this.loadError = true;
            S25Util.showError(
                "Chat is unavailable. Your account may be missing required information. Please contact your administrator.",
            );
        } finally {
            this.cd.detectChanges();
        }
    }

    private buildIframeUrl(tokenHolder: SocJwtResponse) {
        const channel = this.channelId();
        const title = this.channelTitle();
        const token = tokenHolder.token;
        if (!token) {
            console.error("Token is missing from tokenHolder");
            return;
        }
        let url: string = StandoutClassroomService.getBaseWsUrl() + "/embedded/init/chat";

        if (channel) {
            const pathSegment = buildSocChatEmbedPath(this.channelType(), channel, this.socChannelId());
            url += `${pathSegment}?jwt=${encodeURIComponent(token)}`;
            if (title && this.channelType() !== "direct") {
                url += `&title=${encodeURIComponent(title)}`;
            }
            if (this.externalType() && this.channelType() !== "direct") {
                url += `&externalType=${encodeURIComponent(this.externalType()!)}`;
            }
        } else {
            url += `?jwt=${encodeURIComponent(token)}`;
        }

        // Sanitize the URL to prevent Angular security warnings
        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    /**
     * Handle iframe loading errors (e.g., CSP violations)
     */
    onIframeError() {
        console.warn("Iframe failed to load, likely due to CSP restrictions");
        this.showFallback = true;
        this.cd.detectChanges();
    }

    /**
     * Get the proper chat link for opening in a new window/tab
     */
    getChatLink(): string {
        return StandoutClassroomService.getChatLink();
    }
}
